import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useProducts } from 'contexts/ProductsContext';

import p11 from 'assets/wphotos/compressed/11.jpg';
import p12 from 'assets/wphotos/compressed/12.jpg';
import p13 from 'assets/wphotos/compressed/13.jpg';
import p14 from 'assets/wphotos/compressed/14.jpg';
import p21 from 'assets/wphotos/compressed/21.jpg';
import p22 from 'assets/wphotos/compressed/22.jpg';
import p23 from 'assets/wphotos/compressed/23.jpg';
import p24 from 'assets/wphotos/compressed/24.jpg';
import p31 from 'assets/wphotos/compressed/31.jpg';
import p32 from 'assets/wphotos/compressed/32.jpg';
import p33 from 'assets/wphotos/compressed/33.jpg';
import p34 from 'assets/wphotos/compressed/34.jpg';
import p41 from 'assets/wphotos/compressed/41.jpg';
import p42 from 'assets/wphotos/compressed/42.jpg';
import p43 from 'assets/wphotos/compressed/43.jpg';
import p44 from 'assets/wphotos/compressed/44.jpg';
import p45 from 'assets/wphotos/compressed/45.jpg';

const PHOTOS = [p11, p12, p13, p14, p21, p22, p23, p24, p31, p32, p33, p34, p41, p42, p43, p44, p45];

// Map each photo (by index in PHOTOS) to a product handle. Use null for photos
// that don't have a tagged product. Handles must match the Shopify product handle.
const PHOTO_PRODUCT_HANDLES = [
  'lorna-shore-snake-dual-print-oversized-tee', // 11.jpg
  'lorna-shore-snake-dual-print-oversized-tee', // 12.jpg
  'lorna-shore-snake-dual-print-oversized-tee', // 13.jpg
  'lorna-shore-snake-dual-print-oversized-tee', // 14.jpg
  'cob-hate-breeder-oversized-tee', // 21.jpg
  'cob-hate-breeder-oversized-tee', // 22.jpg
  'cob-hate-breeder-oversized-tee', // 23.jpg
  'cob-hate-breeder-oversized-tee', // 24.jpg
  'log-h-oversized-tee', // 31.jpg
  'log-h-oversized-tee', // 32.jpg
  'log-h-oversized-tee', // 33.jpg
  'log-h-oversized-tee', // 34.jpg
  'wh-black-templars-b-oversized-tee', // 41.jpg
  'wh-black-templars-b-oversized-tee', // 42.jpg
  'wh-black-templars-b-oversized-tee', // 43.jpg
  'wh-black-templars-b-oversized-tee', // 44.jpg
  'wh-black-templars-b-oversized-tee', // 45.jpg
];

const SLIDE_INTERVAL = 3500;

function PhotoWidget({ onOpenProduct }) {
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) return undefined;
    const t = setInterval(() => setIndex(i => (i + 1) % PHOTOS.length), SLIDE_INTERVAL);
    return () => clearInterval(t);
  }, [open]);

  return (
    <>
      <Widget
        $hover={hover}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => setOpen(true)}
      >
        <div className="pw__frame">
          {PHOTOS.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className={i === index ? 'pw__img pw__img--active' : 'pw__img'}
              draggable={false}
            />
          ))}
          <div className="pw__label">Real photos</div>
        </div>
      </Widget>
      <PhotoBalloon hover={hover} />
      {open && (
        <PhotoViewer
          startIndex={index}
          onClose={() => setOpen(false)}
          onOpenProduct={onOpenProduct}
        />
      )}
    </>
  );
}

function PhotoBalloon({ hover }) {
  const [autoShow, setAutoShow] = useState(false);
  const [autoVisible, setAutoVisible] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem('binco_photo_balloon_shown')) return;
    const openTimer = setTimeout(() => { setAutoShow(true); setAutoVisible(true); }, 6000);
    const fadeTimer = setTimeout(() => setAutoVisible(false), 16000);
    const closeTimer = setTimeout(() => { setAutoShow(false); sessionStorage.setItem('binco_photo_balloon_shown', '1'); }, 17000);
    return () => { clearTimeout(openTimer); clearTimeout(fadeTimer); clearTimeout(closeTimer); };
  }, []);
  function dismiss() {
    setAutoVisible(false);
    setTimeout(() => setAutoShow(false), 1000);
    sessionStorage.setItem('binco_photo_balloon_shown', '1');
  }
  const show = autoShow || hover;
  const visible = autoVisible || hover;
  if (!show) return null;
  return (
    <BalloonDiv $visible={visible} $hover={hover}>
      <div className="pb__container">
        {!hover && <button className="pb__close" onClick={dismiss} />}
        <div className="pb__title">Real photos of our tees</div>
        <p className="pb__text">Actual shots of the products we sell — not stock images. Click to view.</p>
      </div>
    </BalloonDiv>
  );
}

function PhotoViewer({ startIndex, onClose, onOpenProduct }) {
  const [index, setIndex] = useState(startIndex);
  const { products } = useProducts();

  const prev = useCallback(
    () => setIndex(i => (i - 1 + PHOTOS.length) % PHOTOS.length),
    [],
  );
  const next = useCallback(
    () => setIndex(i => (i + 1) % PHOTOS.length),
    [],
  );

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  const taggedHandle = PHOTO_PRODUCT_HANDLES[index];
  const taggedProduct =
    taggedHandle && products ? products.find(p => p.handle === taggedHandle) : null;

  function handleShopThis() {
    if (!taggedProduct || !onOpenProduct) return;
    onOpenProduct(taggedProduct);
    onClose();
  }

  const stop = e => e.stopPropagation();
  return (
    <Overlay onClick={onClose}>
      <div className="pv__stage">
        <div className="pv__header" onClick={stop}>
          <div className="pv__title">Real photos of binco tees</div>
          <div className="pv__subtitle">What you see is what you get — actual product shots, not stock.</div>
        </div>
        <button className="pv__close" onClick={e => { stop(e); onClose(); }} aria-label="close">×</button>
        <button className="pv__nav pv__nav--prev" onClick={e => { stop(e); prev(); }} aria-label="previous">‹</button>
        <img className="pv__img" src={PHOTOS[index]} alt="" draggable={false} onClick={stop} />
        <button className="pv__nav pv__nav--next" onClick={e => { stop(e); next(); }} aria-label="next">›</button>
        {taggedProduct && (
          <button
            className="pv__tag"
            onClick={e => { stop(e); handleShopThis(); }}
          >
            {taggedProduct.images && taggedProduct.images[0] && (
              <img src={taggedProduct.images[0]} alt="" className="pv__tag__img" />
            )}
            <span className="pv__tag__body">
              <span className="pv__tag__name">{taggedProduct.name}</span>
              <span className="pv__tag__price">₹{taggedProduct.price}</span>
            </span>
            <span className="pv__tag__cta">Shop this →</span>
          </button>
        )}
        <div className="pv__strip" onClick={stop}>
          {PHOTOS.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className={i === index ? 'pv__thumb pv__thumb--active' : 'pv__thumb'}
              onClick={() => setIndex(i)}
              draggable={false}
            />
          ))}
        </div>
      </div>
    </Overlay>
  );
}

const Widget = styled.div`
  position: absolute;
  bottom: 50px;
  right: 20px;
  z-index: 1;
  cursor: pointer;
  width: ${({ $hover }) => ($hover ? '260px' : '130px')};
  height: ${({ $hover }) => ($hover ? '260px' : '130px')};
  transition: width 0.25s ease, height 0.25s ease;

  .pw__frame {
    position: relative;
    width: 100%;
    height: 100%;
    background: #fff;
    border: 1px solid #555;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
    overflow: hidden;
  }
  .pw__img {
    position: absolute;
    inset: 4px;
    width: calc(100% - 8px);
    height: calc(100% - 8px);
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.6s ease;
  }
  .pw__img--active {
    opacity: 1;
  }
  .pw__label {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 3px 6px;
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    color: #fff;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0));
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.6);
    pointer-events: none;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;

  .pv__stage {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 70px 60px 110px;
    box-sizing: border-box;
  }
  .pv__header {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    color: #fff;
    font-family: Tahoma, sans-serif;
    pointer-events: none;
  }
  .pv__title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .pv__subtitle {
    font-size: 12px;
    opacity: 0.75;
    margin-top: 2px;
  }
  .pv__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.6);
  }
  .pv__close {
    position: absolute;
    top: 12px;
    right: 18px;
    width: 36px;
    height: 36px;
    font-size: 28px;
    line-height: 1;
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    cursor: pointer;
    &:hover { background: rgba(255, 255, 255, 0.2); }
  }
  .pv__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 64px;
    font-size: 40px;
    line-height: 1;
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    cursor: pointer;
    &:hover { background: rgba(255, 255, 255, 0.2); }
  }
  .pv__nav--prev { left: 12px; }
  .pv__nav--next { right: 12px; }
  .pv__strip {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 12px;
    display: flex;
    gap: 6px;
    padding: 0 12px;
    overflow-x: auto;
    justify-content: center;
  }
  .pv__thumb {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border: 2px solid transparent;
    cursor: pointer;
    opacity: 0.55;
    flex: 0 0 auto;
    &:hover { opacity: 1; }
  }
  .pv__thumb--active {
    opacity: 1;
    border-color: #fff;
  }
  .pv__tag {
    position: absolute;
    bottom: 110px;
    left: 24px;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 22px 14px 14px;
    background: rgba(20, 20, 20, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    color: #fff;
    cursor: pointer;
    font-family: Tahoma, sans-serif;
    text-align: left;
    max-width: 420px;
    backdrop-filter: blur(4px);
    transition: background 0.15s ease, transform 0.15s ease;
    &:hover {
      background: rgba(40, 40, 40, 0.95);
      transform: translateY(-1px);
    }
  }
  .pv__tag__img {
    width: 84px;
    height: 84px;
    object-fit: cover;
    border-radius: 6px;
    flex: 0 0 auto;
  }
  .pv__tag__body {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .pv__tag__name {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
  }
  .pv__tag__price {
    font-size: 14px;
    opacity: 0.85;
    margin-top: 4px;
  }
  .pv__tag__cta {
    font-size: 13px;
    font-weight: 600;
    margin-left: 12px;
    color: #ffd166;
    white-space: nowrap;
  }
`;

const balloonFadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;
const balloonFadeOut = keyframes`
  0% { opacity: 1; }
  99% { opacity: 0; }
  100% { opacity: 0; display: none; }
`;
const BalloonDiv = styled.div`
  position: absolute;
  bottom: 105px;
  right: ${({ $hover }) => ($hover ? '295px' : '175px')};
  transition: right 0.25s ease;
  z-index: 5;
  opacity: 0;
  animation: ${({ $visible }) => ($visible ? balloonFadeIn : balloonFadeOut)} 1s forwards;
  filter: drop-shadow(2px 2px 1px rgba(0, 0, 0, 0.4));

  .pb__container {
    position: relative;
    border: 1px solid black;
    border-radius: 7px;
    padding: 8px 28px 10px 10px;
    background-color: #ffffe1;
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    max-width: 230px;
    &:before {
      content: '';
      position: absolute;
      top: 18px;
      right: -19px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 10px 0 10px 19px;
      border-color: transparent transparent transparent black;
    }
    &:after {
      content: '';
      position: absolute;
      top: 19px;
      right: -17px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 9px 0 9px 17px;
      border-color: transparent transparent transparent #ffffe1;
    }
  }
  .pb__close {
    outline: none;
    position: absolute;
    right: 4px;
    top: 4px;
    width: 14px;
    height: 14px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    background: transparent;
    cursor: pointer;
    &:before,
    &:after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      height: 8px;
      width: 2px;
      background: rgba(170, 170, 170);
    }
    &:before { transform: rotate(45deg); }
    &:after { transform: rotate(-45deg); }
    &:hover {
      background: #ffa90c;
      border-color: white;
      &:before,
      &:after { background: white; }
    }
  }
  .pb__title {
    font-weight: 700;
    color: #000;
    font-size: 11px;
  }
  .pb__text {
    margin: 5px 0 0;
    color: #333;
    line-height: 1.4;
  }
`;

export default PhotoWidget;
